import { useMemo, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { OldGrid } from "src/components/atoms/OldGrid/OldGrid";
import { OldRow } from "src/components/atoms/OldGrid/OldRow";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import Styles from "./PitchSmart.module.scss";
import { useGetTeamPitchSmartByIDQuery } from "src/state/modules/api";
import { fourDigitNumberFormatter, inningsFormatter, percentFormatter, toStringFormatter } from "./stats-columns/formatters";
import { PlayerNameCell } from "./stats-columns/common-columns";
import PageLoader1 from "src/components/atoms/PageLoader/PageLoader";
import PitchSmartGuidelines from "src/components/atoms/PitchSmart/PitchSmart";
import PitchSmartCards from "src/components/atoms/PitchSmart/PitchSmartCards";
import { useIsMobile } from "src/components/util/useBreakpoint";
import * as Team from "src/state/modules/team";
import {
  PITCH_SMART_BRACKETS,
  PitchSmartTableData,
  ageGroupToBracketId,
  availabilityRank,
  bracketLabel,
  computeAvailability,
  dayCellColor,
  getBracket,
  statusColor,
} from "src/utils/pitch-smart";

function PitchSmart() {
  const { id } = useParams<{ id: string }>();
  const teamID = id ?? "";
  const team = useOutletContext<Team.Team | undefined>();
  const isMobile = useIsMobile();
  // Pitch counts and season stats change as games are played and the backend poller
  // ingests the results. Without these flags RTK Query keeps serving the snapshot it
  // cached on the first visit, so a pitcher who threw today doesn't show up until a
  // full page reload. Refetch when the tab is (re)opened or the window regains focus
  // (setupListeners is wired in store.ts, which enables refetchOnFocus).
  const { data, isLoading } = useGetTeamPitchSmartByIDQuery(teamID, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });
  const { players, pitchSmart, seasonStats } = { ...data };

  const [bracketId, setBracketId] = useState<string | null>(null);
  // Default to the team's age group, but let the coach override it.
  const effectiveBracketId = bracketId ?? ageGroupToBracketId(team?.ageGroup);
  const bracket = getBracket(effectiveBracketId);

  const gridData = useMemo<PitchSmartTableData[]>(() => {
    if (!seasonStats || !players || !pitchSmart) return [];

    const rows = players.map((player): PitchSmartTableData => {
      const playerStats = seasonStats.stats_data.players[player.id]?.stats?.defense;
      const strikeoutPercentage = playerStats?.bf === 0 ? 0.0 : playerStats?.so / playerStats?.bf;
      const counts = [0, 1, 2, 3, 4, 5].map((d) => Number(pitchSmart[d]?.[player.id] ?? 0) || 0);
      const availability = computeAvailability(counts, bracket);
      return {
        id: player.id,
        name: player.first_name + " " + player.last_name + ", " + player.number,
        status: availability.label,
        availability,
        IP: playerStats?.ip,
        "#P": playerStats == null ? 0 : playerStats["#P"],
        "S%": playerStats == null ? 0.0 : playerStats["S%"],
        bf: playerStats == null ? 0 : playerStats.bf,
        so: playerStats == null ? 0 : playerStats.so,
        "SO%": playerStats == null ? 0.0 : strikeoutPercentage,
        WHIP: playerStats == null ? 0.0 : playerStats.whip,
        BAA: playerStats == null ? 0.0 : playerStats.baa,
        TODAY: counts[0],
        DAY1: counts[1],
        DAY2: counts[2],
        DAY3: counts[3],
        DAY4: counts[4],
        DAY5: counts[5],
      };
    });

    // Least available → most available; ties broken by most innings pitched.
    return rows.sort(
      (a, b) => availabilityRank(a.availability) - availabilityRank(b.availability) || (b.IP ?? 0) - (a.IP ?? 0)
    );
  }, [players, pitchSmart, seasonStats, bracket]);

  if (isLoading) return <PageLoader1 />;

  if (!seasonStats || !players || !pitchSmart) {
    return <h1>No Season Data Found</h1>;
  }

  const CELL_WIDTH = 90;
  // Color a day cell by the rest its pitch count triggers in the selected bracket.
  const dayCellStyle = (params: { value: number }) => {
    const bg = dayCellColor(params.value ?? 0, bracket);
    return { textAlign: "center" as const, ...(bg ? { backgroundColor: bg } : {}) };
  };

  return (
    <OldGrid centered={false} className={Styles.statsPageContainer}>
      <OldRow>
        <div className={Styles.controls}>
          <label htmlFor="pitch-smart-bracket" className={Styles.controlsLabel}>
            Pitcher age group
          </label>
          <select
            id="pitch-smart-bracket"
            className={Styles.bracketSelect}
            value={effectiveBracketId}
            onChange={(e) => setBracketId(e.target.value)}
          >
            {PITCH_SMART_BRACKETS.map((b) => (
              <option key={b.id} value={b.id}>
                {bracketLabel(b)} · {b.dailyMax} pitch max
              </option>
            ))}
          </select>
          <span className={Styles.controlsHint}>
            {bracketLabel(bracket)} · {bracket.dailyMax} pitch daily max
            {!bracketId && team?.ageGroup ? ` · from team (${team.ageGroup})` : ""}
          </span>
        </div>
      </OldRow>

      <OldRow>
        {isMobile ? (
          <details className={Styles.guideDetails}>
            <summary className={Styles.guideSummary}>Pitch Smart rest &amp; pitch-count guidelines</summary>
            <div className={Styles.guideScroll}>
              <PitchSmartGuidelines selectedBracketId={effectiveBracketId} />
            </div>
          </details>
        ) : (
          <PitchSmartGuidelines selectedBracketId={effectiveBracketId} />
        )}
      </OldRow>

      <OldRow>
        <div className={Styles.legend}>
          <span className={Styles.legendItem}>
            <span className={Styles.swatch} style={{ background: "#e0edde" }} /> Available
          </span>
          <span className={Styles.legendItem}>
            <span className={Styles.swatch} style={{ background: "#dbe6f2" }} /> Threw today
          </span>
          <span className={Styles.legendItem}>
            <span className={Styles.swatch} style={{ background: "#f1f1da" }} /> 1 day rest
          </span>
          <span className={Styles.legendItem}>
            <span className={Styles.swatch} style={{ background: "#f6ddc8" }} /> 2–3 days rest
          </span>
          <span className={Styles.legendItem}>
            <span className={Styles.swatch} style={{ background: "#f3cccc" }} /> 4+ days rest
          </span>
        </div>
      </OldRow>

      {isMobile ? (
        <OldRow>
          <PitchSmartCards rows={gridData} bracket={bracket} teamID={teamID} />
        </OldRow>
      ) : (
        <OldRow>
        <div className="DataTableOverride__stickyItem">
          <div className="ag-theme-quartz sticky-stats-grid">
            <AgGridReact
              domLayout="autoHeight"
              columnHoverHighlight={true}
              rowData={gridData}
              columnDefs={[
                {
                  field: "name",
                  headerName: "Player",
                  cellStyle: { textAlign: "left" },
                  pinned: "left",
                  // Link each name to the player profile, matching the season/game stats grids.
                  // PlayerNameCell expects a { name, id, teamID, status } value, so build it here.
                  valueGetter: (params) => ({
                    name: params.data?.name,
                    id: params.data?.id,
                    teamID,
                    status: "active",
                  }),
                  valueFormatter: toStringFormatter,
                  cellRenderer: PlayerNameCell,
                },
                {
                  field: "status",
                  headerName: "Status",
                  pinned: "left",
                  width: 170,
                  cellStyle: (params) => {
                    const bg = params.data ? statusColor(params.data.availability) : "";
                    return { textAlign: "left" as const, fontWeight: 600, ...(bg ? { backgroundColor: bg } : {}) };
                  },
                },
                {
                  field: "IP",
                  headerName: "IP",
                  valueFormatter: inningsFormatter,
                  cellStyle: { textAlign: "center" },
                  width: CELL_WIDTH,
                },
                {
                  field: "#P",
                  headerName: "#P",
                  cellStyle: { textAlign: "center" },
                  width: CELL_WIDTH,
                },
                {
                  field: "S%",
                  headerName: "S%",
                  cellStyle: { textAlign: "center" },
                  valueFormatter: percentFormatter,
                  width: CELL_WIDTH,
                },
                {
                  field: "bf",
                  headerName: "BF",
                  cellStyle: { textAlign: "center" },
                  width: CELL_WIDTH,
                },
                {
                  field: "so",
                  headerName: "SO",
                  cellStyle: { textAlign: "center" },
                  width: CELL_WIDTH,
                },
                {
                  field: "SO%",
                  headerName: "SO%",
                  cellStyle: { textAlign: "center" },
                  valueFormatter: percentFormatter,
                  width: CELL_WIDTH,
                },
                {
                  field: "WHIP",
                  headerName: "WHIP",
                  cellStyle: { textAlign: "center" },
                  width: CELL_WIDTH,
                  valueFormatter: fourDigitNumberFormatter,
                },
                {
                  field: "BAA",
                  headerName: "BAA",
                  cellStyle: { textAlign: "center" },
                  width: CELL_WIDTH,
                  valueFormatter: fourDigitNumberFormatter,
                },
                {
                  field: "TODAY",
                  headerName: "TODAY",
                  cellStyle: dayCellStyle,
                  width: CELL_WIDTH,
                },
                {
                  field: "DAY1",
                  headerName: "DAY(-1)",
                  cellStyle: dayCellStyle,
                  width: CELL_WIDTH,
                },
                {
                  field: "DAY2",
                  headerName: "DAY(-2)",
                  cellStyle: dayCellStyle,
                  width: CELL_WIDTH,
                },
                {
                  field: "DAY3",
                  headerName: "DAY(-3)",
                  cellStyle: dayCellStyle,
                  width: CELL_WIDTH,
                },
                {
                  field: "DAY4",
                  headerName: "DAY(-4)",
                  cellStyle: dayCellStyle,
                  width: CELL_WIDTH,
                },
                {
                  field: "DAY5",
                  headerName: "DAY(-5)",
                  cellStyle: dayCellStyle,
                  width: CELL_WIDTH,
                },
              ]}
            />
          </div>
        </div>
        </OldRow>
      )}
    </OldGrid>
  );
}

export default PitchSmart;
